import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { getEvent, verifyEventAttendance } from '@/lib/events-api';
import { ArrowLeft, Shield, Check, MapPin, Clock, Hash, AlertTriangle, CheckCircle } from 'lucide-react';

export default function VerifyAttendancePage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';
  const { ready } = useRequireAuth();

  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null);
  const [error, setError] = useState(null);
  const [simLat, setSimLat] = useState('30.267153');
  const [simLng, setSimLng] = useState('-97.743057');
  const [simAcc, setSimAcc] = useState('25');

  useEffect(() => {
    if (!eventId || !ready) return;
    (async () => {
      try { const b = await getEvent(eventId); setEvent(b.event); }
      catch (e) { setError(e.message || "Couldn't load event."); }
      finally { setLoadingEvent(false); }
    })();
  }, [eventId, ready]);

  const eventDate = useMemo(() => {
    if (!event?.start_at) return '';
    const d = new Date(event.start_at);
    return isNaN(d.getTime()) ? event.start_at : d.toLocaleString();
  }, [event]);

  async function doVerify(lat, lng, accuracy) {
    setError(null); setBusy(true); setDecision(null);
    try {
      const result = await verifyEventAttendance(eventId, { lat, lng, accuracy_m: accuracy, client_timestamp: new Date().toISOString() });
      setDecision(result);
      if (result.decision === 'VERIFIED') setTimeout(() => navigate('/contrib-event', { replace: true }), 1200);
    } catch (e) { setError(e.message || 'Verification failed.'); }
    finally { setBusy(false); }
  }

  async function onAllowLocation() {
    if (!navigator.geolocation) { setError("Geolocation not supported. Use the simulate panel below."); return; }
    navigator.geolocation.getCurrentPosition(
      pos => doVerify(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy || 25),
      () => setError("Couldn't read location. Use the simulate panel below."),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  }

  function onSubmitSimulate() {
    const lat = parseFloat(simLat), lng = parseFloat(simLng), acc = parseFloat(simAcc);
    if (isNaN(lat) || isNaN(lng) || isNaN(acc)) { setError('Enter valid coordinates.'); return; }
    doVerify(lat, lng, acc);
  }

  if (!eventId) return <div className="p-8 text-[#5B6472]">Missing event.</div>;

  return (
    <div className="max-w-lg mx-auto px-4 py-6" data-testid="verify-attendance-screen">
      <button onClick={() => navigate('/contrib-event', { replace: true })} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="verify-back-btn">
        <ArrowLeft className="w-4 h-4" /> back
      </button>

      <div className="flex justify-center mb-4">
        <div className="w-20 h-20 rounded-full bg-[#EFF5FF] flex items-center justify-center relative">
          <Shield className="w-10 h-10 text-[#146BFF]" />
          <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#1F8A55] flex items-center justify-center border-2 border-white">
            <Check className="w-4 h-4 text-white" />
          </div>
        </div>
      </div>
      <h1 className="text-2xl font-extrabold text-[#0B1B46] text-center mb-1" data-testid="verify-title">Verify Attendance</h1>
      <p className="text-sm text-[#5B6472] text-center mb-4" data-testid="verify-explainer">Your location protects the host from outside submissions.</p>

      {loadingEvent ? (
        <div className="flex justify-center py-4" data-testid="verify-loading"><div className="w-6 h-6 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : event ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="verify-event-card">
          <p className="text-lg font-extrabold text-[#0B1B46] mb-2" data-testid="verify-event-name">{event.name}</p>
          <div className="flex flex-col gap-1 text-sm text-[#5B6472]">
            <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5" /><span data-testid="verify-event-venue">{event.venue_name}{event.venue_address ? `\n${event.venue_address}` : ''}</span></div>
            <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" /><span>{eventDate}</span></div>
            {event.current_event_id && <div className="flex items-center gap-2"><Hash className="w-3.5 h-3.5 text-[#1F8A55]" /><span className="px-2 py-0.5 bg-[#E4F4EC] text-[#1F8A55] text-xs font-bold rounded-full" data-testid="verify-event-code">{event.current_event_id}</span></div>}
          </div>
        </div>
      ) : null}

      <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="verify-allow-card">
        <div className="flex items-start gap-3 mb-3">
          <MapPin className="w-5 h-5 text-[#146BFF] mt-0.5" />
          <div>
            <p className="font-bold text-[#0B1B46]">Allow Location Access</p>
            <p className="text-sm text-[#5B6472]">We check that you're within the event radius. Your coordinates are never shared.</p>
          </div>
        </div>
        <button onClick={onAllowLocation} disabled={busy}
          className="w-full h-10 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg mb-2 transition-colors"
          data-testid="verify-allow-btn">{busy ? 'Checking…' : 'Allow Location'}</button>
        <button onClick={() => navigate(`/host-approval-required?event_id=${eventId}`, { replace: true })}
          className="w-full h-10 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
          data-testid="verify-not-now-btn">Not Now</button>
      </div>

      {/* DEV simulate */}
      <div className="bg-[#FBEEDA] border border-[#B86500]/20 rounded-xl p-4 mb-4" data-testid="verify-dev-panel">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 bg-[#B86500] text-white text-[10px] font-bold rounded">DEV ONLY</span>
          <span className="font-bold text-[#0B1B46] text-sm">Simulate coordinates</span>
        </div>
        <p className="text-sm text-[#5B6472] mb-3">Venue is ~30.267153, -97.743057 (500m radius).</p>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div><label htmlFor="sim-lat" className="text-xs font-semibold text-[#0B1B46]">Latitude</label><input id="sim-lat" value={simLat} onChange={e => setSimLat(e.target.value)} className="w-full h-9 px-3 border border-[#E3E8EF] rounded-lg text-sm font-mono" data-testid="verify-sim-lat" /></div>
          <div><label htmlFor="sim-lng" className="text-xs font-semibold text-[#0B1B46]">Longitude</label><input id="sim-lng" value={simLng} onChange={e => setSimLng(e.target.value)} className="w-full h-9 px-3 border border-[#E3E8EF] rounded-lg text-sm font-mono" data-testid="verify-sim-lng" /></div>
        </div>
        <div className="mb-2"><label htmlFor="sim-acc" className="text-xs font-semibold text-[#0B1B46]">Accuracy (m)</label><input id="sim-acc" value={simAcc} onChange={e => setSimAcc(e.target.value)} className="w-full h-9 px-3 border border-[#E3E8EF] rounded-lg text-sm font-mono" data-testid="verify-sim-accuracy" /></div>
        <button onClick={onSubmitSimulate} disabled={busy}
          className="w-full h-10 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] disabled:opacity-50 transition-colors"
          data-testid="verify-sim-btn">{busy ? 'Simulating…' : 'Simulate Location'}</button>
      </div>

      {decision && (
        <div className={`border rounded-xl p-4 mb-4 ${decision.decision === 'VERIFIED' ? 'bg-[#E4F4EC] border-[#1F8A55]/20' : 'bg-[#FBEEDA] border-[#B86500]/20'}`} data-testid="verify-decision-card">
          <div className="flex items-center gap-2 mb-1">
            {decision.decision === 'VERIFIED' ? <CheckCircle className="w-5 h-5 text-[#1F8A55]" /> : <AlertTriangle className="w-5 h-5 text-[#B86500]" />}
            <span className="font-bold text-[#0B1B46]" data-testid="verify-decision-label">{decision.decision.replace(/_/g, ' ')}</span>
          </div>
          <p className="text-sm text-[#5B6472]" data-testid="verify-decision-reason">{decision.reason}</p>
          {decision.decision === 'VERIFIED' ? (
            <span className="inline-block mt-2 px-2.5 py-0.5 bg-[#E4F4EC] text-[#1F8A55] text-xs font-bold rounded-full" data-testid="verify-unlocked-badge">Uploads unlocked</span>
          ) : (
            <button onClick={() => navigate(`/host-approval-required?event_id=${eventId}`, { replace: true })}
              className="w-full h-10 mt-2 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
              data-testid="verify-request-approval-btn">Request Host Approval</button>
          )}
        </div>
      )}

      {error && <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434] mb-4" data-testid="verify-error">{error}</div>}
      <p className="text-center text-xs text-[#5B6472] italic" data-testid="verify-privacy-footer">Your live location is never shared with other attendees.</p>
    </div>
  );
}
